import type React from 'react';
import { useMemo, useState } from 'react';
import { Switch, Route, Link, useLocation, useHistory, Redirect } from 'react-router-dom';
import ProLayout, { type ProLayoutProps } from '@ant-design/pro-layout';
import { ThemeSwitcher } from '@enmon-monorepo/ui';
import { IS_THEME_SWITCHER_ENABLED } from 'containers/ant-config-provider/AppAntConfigProvider';
import { Button, Col, ConfigProvider, Dropdown, Row } from 'antd';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { useApolloClient } from '@apollo/client';
import { useQueryClient } from 'react-query';
import { sessionState } from 'store';
// @ts-expect-error - icon is not exported from 'images/logo-icon.svg?react'
import LogoIcon from 'images/logo-icon.svg?react';
import enUS from 'antd/lib/locale/en_US';
import { ResellersRouter } from 'routes/resellers/Router';
import { LoginPage } from 'containers/public/LoginPage';
import type { StrictOmit } from '@enmon/commons';
import PortfolioRouter from 'routes/portfolio';
import { Dashboard } from 'routes/dashboard/Page';
import { ResellerDataRouter } from 'routes/reseller-data/Router';
import { ExamplesRouter } from 'routes/examples/Router';
import ToolboxRouter from 'routes/toolbox';
import { LAYOUT_CONTENT_PADDING } from './constants';
import { useRoutes } from './useRoutes';
import { getAllRoutePaths } from './utils/getRoutePaths';
import { getLongestPathKeyMatch } from './utils/getLongestKeyMatch';
import { createPathnameKeys } from './utils/createPathnameKeys';

const StyledProLayout = styled(ProLayout)`
  height: 100%;

  &&& {
    .ant-menu {
      padding: 8px;
      .ant-menu-item {
        background-color: ${({ theme }) => theme.color.grey.white};
        margin-inline: 0px;
        margin-block: 0px;
        width: 100%;
        padding: 0 12px !important;
        :hover {
          color: ${({ theme }) => theme.color.theme.primary};
          background-color: ${({ theme }) => theme.color.grey.white};
        }
      }
      .ant-menu-submenu-title {
        padding: 0 12px !important;
        margin: 0px;
        width: 100%;
      }
      .ant-pro-base-menu-inline-item-title {
        gap: 3px;
      }

      .ant-menu-item-selected {
        color: ${({ theme }) => theme.color.theme.primary};
        background-color: ${({ theme }) => theme.color.theme.lighterAlt};
        border-right: 3px solid ${({ theme }) => theme.color.theme.primary};
        :hover {
          background-color: ${({ theme }) => theme.color.theme.lighterAlt};
        }
      }
    }
    .ant-menu-submenu,
    .ant-menu-submenu-title {
      background-color: ${({ theme }) => theme.color.grey.white};
      :hover {
        color: ${({ theme }) => theme.color.theme.primary};
        background-color: ${({ theme }) => theme.color.grey.white};
      }
    }
  }

  .any-layout-header {
    z-index: 0;
  }

  .ant-layout-sider-children {
    background-color: white;
    padding-inline: 0px !important;
  }

  .ant-pro-layout-container {
    overflow: hidden;
  }

  .ant-layout-content {
    overflow: auto;
    background-color: #f0f2f5;
    padding: ${LAYOUT_CONTENT_PADDING}px;
  }

  > .ant-layout {
    max-height: 100%;

    #logo {
      h1 {
        color: ${({ theme }) => theme.color.type.primary};
      }
    }
  }
`;

export const AuthenticatedApp: React.VFC = () => {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const [{ userInfo }, setSession] = useRecoilState(sessionState);

  const routes = useRoutes();
  const location = useLocation();
  const history = useHistory();

  const handleLogoutButtonClick = () => {
    queryClient.clear();
    apolloClient.clearStore();
    setSession({ isAuthenticated: false });
  };

  const userDropdown = (
    <Dropdown
      menu={{
        items: [
          {
            key: 'logout',
            label: 'Logout',
            onClick: handleLogoutButtonClick,
          },
        ],
      }}
    >
      <Button>{userInfo?.username}</Button>
    </Dropdown>
  );

  const header = (
    <Row align="middle" gutter={16} justify="end" style={{ margin: '0 16px', zIndex: 0 }}>
      {
        // Opt-in only:  Hidden because dark mode is unfinished.
        IS_THEME_SWITCHER_ENABLED && (
          <Col>
            <ThemeSwitcher size="sm" />
          </Col>
        )
      }

      <Col>{userDropdown}</Col>
    </Row>
  );

  const allRoutePaths = useMemo(() => getAllRoutePaths(routes), [routes]);

  const props: StrictOmit<ProLayoutProps, 'theme'> = useMemo(
    () => ({
      disableMobile: true, // add Open Menu button into header if you want to enable mobile view
      route: routes,
      // location was causing and issue with maximum call stack exceeded
      location: {},
      // get longest path key match from pathname to one of the routes, so side nav shows correct active item
      selectedKeys: getLongestPathKeyMatch(createPathnameKeys(location.pathname), allRoutePaths),
      navTheme: 'light',
      collapsed,
      locale: 'en-US',
      title: 'Enmon Tools',
      logo: <LogoIcon />,
      onCollapse: setCollapsed,
      menuItemRender: (item, dom) => <Link to={item.path}>{dom}</Link>,
      headerContentRender: () => header,
      layout: 'mix',
      onMenuHeaderClick: () => history.push('/'),
    }),
    [allRoutePaths, collapsed, header, history, location.pathname, routes],
  );

  return userInfo ? (
    <StyledProLayout {...props}>
      {/* ConfigProvider is required here to override ProLayout providers with chinese form translations. */}
      <ConfigProvider locale={enUS}>
        <Switch>
          <Route exact component={Dashboard} path="/" />
          <Route component={PortfolioRouter} path="/portfolio" />
          <Route component={ResellerDataRouter} path="/reseller-data" />
          <Route component={ResellersRouter} path="/resellers" />
          <Route component={ExamplesRouter} path="/examples" />
          <Route component={ToolboxRouter} path="/toolbox" />
          <Route path="/inventories/meters/:id/details">
            <Redirect to="/portfolio/meters/:id/details" />
          </Route>
          <Route render={() => <>Page not found</>} />
        </Switch>
      </ConfigProvider>
    </StyledProLayout>
  ) : (
    <LoginPage />
  );
};
